import { isEmpty } from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input } from "@mui/material";
import { IMaskInput } from "react-imask";
import { t } from "i18next";

import { Icon, CustomTooltip } from "components";
import {
  getButtonBgColor,
  setLabelItems,
  selectLabelItems,
  selectIsFaq,
  addNewMessage,
  selectIsDisabled,
  getQualificationButton,
  getJaiEditUrl,
  selectIsJAI,
} from "slices";
import { validator } from "utils";
import { sendHayleyMessage } from "utils/methods/index";

import ExtraOption from "../ExtraOptions/index";

const PhoneMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask="(#00) 000-0000"
      definitions={{ "#": /[1-9]/ }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      onChange={() => {}}
      overwrite
    />
  );
});

const InputView = ({ replies, onClick }) => {
  const dispatch = useDispatch();
  const backgroundColor = useSelector(getButtonBgColor);
  const prevLabel = useSelector(selectLabelItems);
  const isFaq = useSelector(selectIsFaq);
  const jaiEditURL = useSelector(getJaiEditUrl);
  const isJAI = useSelector(selectIsJAI);
  const optionDisabled = useSelector(selectIsDisabled);
  const hayleyQualification = useSelector(getQualificationButton);

  const paramObj = { ...prevLabel };

  const [inputValue, setInputValue] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRedbordered, setIsRedbordered] = useState(false);

  const inputRef = useRef(null);

  const repliesOptions = replies?.options[0] || {};

  const {
    content: { componentOptions: { type = null, placeholder = null, callbackOptions = null, validate = null } = {} } = {},
    nextOptionsType,
    key,
    isRequired,
  } = repliesOptions;

  const checkKey = key === "phone" ? "phone_number" : key;

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.querySelector("input")?.focus();
    }, [300]);
  }, []);

  useEffect(() => {
    setInputValue(prevLabel[key] || "");
  }, [replies]);

  useEffect(() => {
    if (inputValue === "") {
      setDisabled(true);
      setIsRedbordered(false);
      setErrorMessage(t("enterValue", isFaq ? { lng: "en" } : {}));
    } else if (validate) {
      setDisabled(!validator(inputValue, validate));
      setIsRedbordered(!validator(inputValue, validate));
      setErrorMessage(validator(inputValue, validate) ? "" : t("enterValidValue", isFaq ? { lng: "en" } : {}));
    } else {
      setDisabled(false);
      setIsRedbordered(false);
      setErrorMessage("");
    }
  }, [inputValue, isFaq]);

  const onChange = (e) => {
    const { value } = e.target;

    if (validate === "TEXT_ONLY") {
      if (validator(value, validate) || isEmpty(value)) {
        setInputValue(value);
      }
    } else {
      if (/^\d*\.?\d*$/.test(value)) {
        setInputValue(value);
      }
    }
  };

  const extraButtons = isJAI
    ? [
        {
          text: t("editSubOrg"),
          handleClick: () => window.open(jaiEditURL, "_blank"),
        },
      ]
    : null;

  const onClickSend = () => {
    const cbOptions = callbackOptions ? { ...callbackOptions, data: { ...callbackOptions.data, value: inputValue } } : undefined;

    dispatch(addNewMessage([{ message: inputValue, speaker: "user", createdAt: new Date() }]));

    const eventHash = {
      event_name: "h_fill_forms", // or "h_submit_form"
      event_params: {
        h_page_type: "hayley",
        h_clicked_location: "contact_us_submit",
        h_stream: "hyly",
        // h_tour_type: label,
        h_display_method: "iframe",
      },
    };

    if (replies?.optionsType === "DoIQualify") {
      sendHayleyMessage({
        text: inputValue || "",
        queryType: "question",
        ownerType: "prospect",
        kgNodeValue: "do i qualify",
      });

      dispatch(
        addNewMessage([
          {
            message: parseInt(inputValue.replace(/[^\d]/g, "")) >= parseInt(hayleyQualification.qualification) ? "qualified" : "unqualified",
            speaker: "bot",
            createdAt: new Date(),
          },
        ])
      );

      sendHayleyMessage({
        text: parseInt(inputValue.replace(/[^\d]/g, "")) >= parseInt(hayleyQualification.qualification) ? t("qualified") : t("unqualified") || "",
        queryType: "answer",
        ownerType: "agent",
        kgNodeValue: "do i qualify",
      });
    }

    window.parent.postMessage(JSON.stringify(eventHash), "*");

    paramObj[checkKey] = inputValue;
    dispatch(setLabelItems(paramObj));
    onClick([{ message: inputValue, speaker: "user", createdAt: new Date() }], nextOptionsType, false, cbOptions);
  };

  const onKeyPress = (e) => {
    if (e.key === "Enter" && !disabled && !optionDisabled) {
      onClickSend();
    }
  };

  return (
    <>
      {!isEmpty(replies?.title) && (
        <div dangerouslySetInnerHTML={{ __html: replies?.title }} className="hayley-mb-3 hayley-w-full hayley-flex-none hayley-text-sm hayley-text-black" />
      )}

      <div className="hayley-flex hayley-w-full hayley-items-center hayley-space-x-4">
        <div
          className={`hayley-flex hayley-h-[56px] hayley-w-full hayley-flex-1 hayley-flex-col hayley-justify-center hayley-rounded-[2px] hayley-border hayley-border-solid ${
            isRedbordered ? "hayley-border-red-300" : "hayley-border-gray-300"
          } hayley-bg-white hayley-px-[10px] hayley-py-[6px] hayley-font-opensans hayley-text-sm hayley-leading-6 hayley-text-secondary placeholder:hayley-text-gray-400`}
        >
          {!replies && inputValue && <label className="hayley-mb-2 hayley-h-3 hayley-text-gray-600">{`${t(placeholder)} ${isRequired ? "*" : ""}`}</label>}
          <Input
            data-testid="test-input"
            className={`hayley-border-none focus:hayley-outline-none`}
            type={type || "text"}
            placeholder={`${t(placeholder || "enterQuesHere")} ${isRequired ? "*" : ""}`}
            disabled={optionDisabled}
            value={inputValue}
            onChange={onChange}
            onKeyDown={onKeyPress}
            ref={inputRef}
            inputComponent={validate === "PHONE" ? PhoneMaskCustom : undefined}
            disableUnderline
            multiline
            maxRows={2}
          />
        </div>

        <CustomTooltip title={errorMessage} placement="left">
          <span>
            <button
              className={`hayley-flex hayley-h-11 hayley-w-11 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full hayley-text-white hayley-border-none${
                !disabled ? "" : " hayley-opacity-10"
              }`}
              style={{ backgroundColor }}
              onClick={onClickSend}
              disabled={optionDisabled || disabled}
              data-testid="contactUsSend"
            >
              <Icon iconType="Send" className="-hayley-ml-1 hayley-h-[34px] hayley-w-[34px] hayley-text-inherit" />
            </button>
          </span>
        </CustomTooltip>
      </div>
      {/* {isLastBot ? <ExtraOption from="inputView" extraButtons={extraButtons} /> : null} */}
      <ExtraOption from="inputView" extraButtons={extraButtons} />
    </>
  );
};

export default InputView;
